import type { AssetStatus, ItemProps, SpreadsheetMimeType } from "@bedrock/domain";

export const XLSX_MIME_TYPE: SpreadsheetMimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const XLS_MIME_TYPE: SpreadsheetMimeType = "application/vnd.ms-excel";
export const ASSET_FILE_ACCEPT = `image/png,image/jpeg,image/webp,image/gif,image/heic,video/mp4,video/webm,video/quicktime,application/pdf,.pdf,${XLSX_MIME_TYPE},${XLS_MIME_TYPE},.xlsx,.xls`;
export const IMPORTABLE_FILE_ACCEPT = `${ASSET_FILE_ACCEPT},text/markdown,text/plain,.md,.txt`;

type AssetFile = Pick<File, "name" | "type">;

export function canvasAssetFileKind(file: AssetFile): "image" | "video" | "pdf" | "spreadsheet" | null {
  if (/\.xlsx$/i.test(file.name) || /\.xls$/i.test(file.name)) return "spreadsheet";
  if (/\.(png|jpe?g|webp|gif|heic)$/i.test(file.name)) return "image";
  if (/\.(mp4|webm|mov)$/i.test(file.name)) return "video";
  if (/\.pdf$/i.test(file.name)) return "pdf";
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.toLocaleLowerCase() === "application/pdf") return "pdf";
  if (file.type.toLocaleLowerCase() === XLSX_MIME_TYPE || file.type.toLocaleLowerCase() === XLS_MIME_TYPE) return "spreadsheet";
  return null;
}

export function spreadsheetMimeTypeForFile(file: AssetFile): SpreadsheetMimeType | null {
  if (/\.xlsx$/i.test(file.name)) return XLSX_MIME_TYPE;
  if (/\.xls$/i.test(file.name)) return XLS_MIME_TYPE;
  const mimeType = file.type.toLocaleLowerCase();
  if (mimeType === XLSX_MIME_TYPE || mimeType === XLS_MIME_TYPE) return mimeType;
  return null;
}

export function canvasAssetPropsForFile(file: AssetFile, assetId: string, status: AssetStatus, localUrl: string): ItemProps | null {
  const kind = canvasAssetFileKind(file);
  if (kind === "image") return { kind, assetId, filename: file.name || "clipboard-image", mimeType: file.type, alt: "", status, localUrl };
  if (kind === "video") return { kind, assetId, filename: file.name || "clipboard-video", mimeType: file.type, status, posterAssetId: null, localUrl };
  if (kind === "pdf") return { kind, assetId, filename: file.name || "clipboard-document.pdf", mimeType: "application/pdf", status, localUrl };
  if (kind === "spreadsheet") {
    const mimeType = spreadsheetMimeTypeForFile(file) ?? XLSX_MIME_TYPE;
    return { kind, assetId, filename: file.name || `clipboard-workbook.${mimeType === XLS_MIME_TYPE ? "xls" : "xlsx"}`, mimeType, status, localUrl };
  }
  return null;
}
